import { createContext, useCallback, useContext, useEffect } from 'react';
import { getConsult } from 'api/consult/getConsult';
import { PatientConsult } from 'api/consult/types/consults.types';
import {
  CareCycleState,
  generateConsultState,
  generatePatientState,
  getConsultCareCycleStep,
  InferredConsultState,
  InferredPatientState,
} from 'api/consult/types/consultStates';
import {
  updatePatientConsultState,
  UpdatePatientConsultStateParams,
} from 'api/survey/updatePatientConsultState';
import { FCWithChildren } from 'helpers/types/FCWithChildren';
import { FeatureFlags, useFlag } from 'hooks/useFlag';
import { useMergeState } from 'hooks/useMergeState';

import { useCurrentUser } from './CurrentUserContext';

export enum ConsultSteps {
  Start = 'start',
  SymptomAssessment = 'symptom-assessment',
  MedicalHistory = 'medical-history',
  ChildVideo = 'child-video',
  ParentVideo = 'parent-video',
}

interface ConsultState {
  careCycleStep: CareCycleState | null;
  consult?: PatientConsult;
  consultState?: InferredConsultState;
  patientState?: InferredPatientState;
}

export interface IConsultContext extends ConsultState {
  updateState: (
    args: UpdatePatientConsultStateParams,
  ) => Promise<PatientConsult>;
  refresh: () => Promise<PatientConsult>;
}

const ConsultContext = createContext<IConsultContext>({
  careCycleStep: null,
  updateState: () => Promise.reject(),
  refresh: () => Promise.reject(),
});

export const ConsultProvider: FCWithChildren<{ consultId: string }> = ({
  consultId,
  children,
}) => {
  const [state, setState] = useMergeState<ConsultState>({
    careCycleStep: null,
  });

  const { state: userState } = useCurrentUser();
  const account = userState.account;

  const showMemberFinancialPolicy = useFlag(
    FeatureFlags.DisplayMemberFinancialPolicy,
  );

  const setConsult = useCallback(
    (consult: PatientConsult) => {
      setState({
        consult,
        consultState: generateConsultState(consult),
        patientState: generatePatientState(consult.patient),
        careCycleStep: getConsultCareCycleStep(
          consult,
          showMemberFinancialPolicy,
        ),
      });
    },
    [setState, showMemberFinancialPolicy],
  );

  useEffect(() => {
    if (!account) {
      return;
    }

    getConsult(consultId).then(setConsult);
  }, [account, consultId, setConsult]);

  const refresh = () =>
    getConsult(consultId).then(consult => {
      setConsult(consult);
      return consult;
    });

  const updateState = async (args: UpdatePatientConsultStateParams) => {
    const consultResponse = await updatePatientConsultState(args);
    const consult: PatientConsult = {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...state.consult!,
      patientState: consultResponse.patientState,
    };
    setConsult(consult);
    return consult;
  };

  const ctx: IConsultContext = {
    ...state,
    refresh,
    updateState,
  };

  return (
    <ConsultContext.Provider value={ctx}>{children}</ConsultContext.Provider>
  );
};

export const useConsult = () => {
  const context = useContext<IConsultContext>(ConsultContext);

  if (!context)
    throw new Error(
      '!--- useConsult must be used inside a ConsultProvider ---!',
    );

  return context;
};
